/**
 * CalendarEventEditPicker - Wraps children (DateTimeBox pair) with a
 * DateTimeRangePicker popover for editing start/end times.
 *
 * Uses react-aria DialogTrigger so the children act as the trigger element.
 */

import {
  parseAbsolute,
  Time,
  toCalendarDate,
  toCalendarDateTime,
  today,
  toTime,
  toZoned,
} from '@internationalized/date';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import type { DateValue, TimeValue } from 'react-aria-components';
import {
  Button as AriaButton,
  Dialog as AriaDialog,
  DialogTrigger,
  Popover as AriaPopover,
} from 'react-aria-components';

import { StackingLayer, useStackingZIndex } from '@/contexts/StackingContext';
import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import { Button } from '@/lib/base/buttons/button';
import { Calendar } from '@/lib/base/pickers/date-picker/calendar';
import { TimeField } from '@/lib/base/pickers/time-picker/time-field';
import { useOrgTimeFormat } from '@/lib/time';
import { cx } from '@/utils/cx';

interface CalendarEventEditPickerProps {
  startTime: string | null;
  endTime: string | null;
  onChange: (range: { start: Date | null; end: Date | null }) => void;
  children: ReactNode;
}

/**
 * Parse an ISO datetime string into CalendarDate and Time values.
 *
 * `null` (an unresolved time) is handled explicitly before touching `Date` —
 * `new Date(null)` resolves to the 1970 epoch instead of throwing, so passing
 * it through unchanged would seed the picker with a bogus date.
 */
function parseDateTimeParts(isoString: string | null, tz: string) {
  if (!isoString) {
    return { date: null, time: null };
  }
  try {
    const zdt = parseAbsolute(new Date(isoString).toISOString(), tz);
    return {
      date: toCalendarDate(zdt),
      time: toTime(zdt),
    };
  } catch {
    return { date: null, time: null };
  }
}

// ─── Inner dialog (extracted to allow stable useCallback on close-dependent handlers) ──

interface CalendarPickerDialogProps {
  draftDate: DateValue | null;
  draftStartTime: TimeValue | null;
  draftEndTime: TimeValue | null;
  setDraftDate: (date: DateValue | null) => void;
  setDraftStartTime: (time: TimeValue | null) => void;
  setDraftEndTime: (time: TimeValue | null) => void;
  syncDraft: () => void;
  draftToDateRange: () => { start: Date | null; end: Date | null };
  onChange: (range: { start: Date | null; end: Date | null }) => void;
  close: () => void;
}

function CalendarPickerDialog({
  draftDate,
  draftStartTime,
  draftEndTime,
  setDraftDate,
  setDraftStartTime,
  setDraftEndTime,
  syncDraft,
  draftToDateRange,
  onChange,
  close,
}: CalendarPickerDialogProps) {
  // Org default read from context; passed explicitly only across the
  // lib/base boundary (TimeField must stay org-context-free).
  const { hourCycle } = useOrgTimeFormat();
  const handleCancel = useCallback(() => {
    syncDraft();
    close();
  }, [syncDraft, close]);

  const handleApply = useCallback(() => {
    onChange(draftToDateRange());
    close();
  }, [onChange, draftToDateRange, close]);

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      {/* Body scrolls when the popover is taller than the space available;
          the footer below stays pinned so Apply is always reachable. */}
      <div className='flex min-h-0 flex-1 flex-col overflow-y-auto sm:flex-row'>
        <div className='px-4 py-4 sm:px-6 sm:py-5'>
          <Calendar
            aria-label='Select date'
            value={draftDate}
            onChange={setDraftDate}
          />
        </div>
        <div className='border-t sm:border-t-0 sm:border-l border-secondary p-4 sm:p-6 space-y-4 flex-shrink-0'>
          <TimeField
            value={draftStartTime}
            onChange={setDraftStartTime}
            hourCycle={hourCycle}
            label='Start time'
          />
          <TimeField
            value={draftEndTime}
            onChange={setDraftEndTime}
            hourCycle={hourCycle}
            label='End time'
          />
        </div>
      </div>
      <div className='grid grid-cols-2 gap-3 border-t border-secondary p-4 flex-shrink-0'>
        <Button size='md' color='secondary' onClick={handleCancel}>
          Cancel
        </Button>
        <Button size='md' color='primary' onClick={handleApply}>
          Apply
        </Button>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function CalendarEventEditPicker({
  startTime,
  endTime,
  onChange,
  children,
}: CalendarEventEditPickerProps) {
  // The picker's wall-clock times are displayed AND interpreted in the
  // viewer's profile timezone — matching the DateTimeBox display beside it —
  // never the browser's (FRONTEND_PATTERNS.md § Timezones). Picking "10:00"
  // means 10:00 in YOUR timezone regardless of the machine's locale.
  const tz = useDisplayTimezone();
  const zIndex = useStackingZIndex();

  // Parse current values from props — used as initial draft state
  const parsed = useMemo(() => {
    const start = parseDateTimeParts(startTime, tz);
    const end = parseDateTimeParts(endTime, tz);
    return {
      date: start.date,
      startTime: start.time,
      endTime: end.time,
    };
  }, [startTime, endTime, tz]);

  const [draftDate, setDraftDate] = useState<DateValue | null>(parsed.date);
  const [draftStartTime, setDraftStartTime] = useState<TimeValue | null>(
    parsed.startTime
  );
  const [draftEndTime, setDraftEndTime] = useState<TimeValue | null>(
    parsed.endTime
  );

  // Re-sync draft state from props when the popover opens
  const syncDraft = useCallback(() => {
    setDraftDate(parsed.date ?? today(tz));
    setDraftStartTime(parsed.startTime);
    setDraftEndTime(parsed.endTime);
  }, [parsed, tz]);

  const draftToDateRange = useCallback(() => {
    if (!draftDate || !draftStartTime || !draftEndTime) {
      return { start: null, end: null };
    }
    const startDt = toZoned(
      toCalendarDateTime(
        draftDate,
        new Time(draftStartTime.hour, draftStartTime.minute, 0, 0)
      ),
      tz
    );
    // If end time is before start time, assume overnight — use the next day
    const endIsBeforeStart =
      draftEndTime.hour < draftStartTime.hour ||
      (draftEndTime.hour === draftStartTime.hour &&
        draftEndTime.minute < draftStartTime.minute);
    const endDate = endIsBeforeStart ? draftDate.add({ days: 1 }) : draftDate;
    const endDt = toZoned(
      toCalendarDateTime(
        endDate,
        new Time(draftEndTime.hour, draftEndTime.minute, 0, 0)
      ),
      tz
    );
    return { start: startDt.toDate(), end: endDt.toDate() };
  }, [draftDate, draftStartTime, draftEndTime, tz]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (isOpen) {
        syncDraft();
      }
    },
    [syncDraft]
  );

  return (
    <DialogTrigger>
      <AriaButton className='w-full cursor-pointer text-left outline-none'>
        {children}
      </AriaButton>
      <AriaPopover
        offset={8}
        placement='bottom start'
        shouldFlip
        containerPadding={16}
        onOpenChange={handleOpenChange}
        className={({ isEntering, isExiting }) =>
          cx(
            'origin-(--trigger-anchor-point) will-change-transform',
            // The visual container and the height clamp live HERE, on the
            // popover, not on the inner Dialog. react-aria writes the space
            // actually available to this element straight onto it
            // (`useOverlayPosition` → `overlay.style.maxHeight`), computed
            // after `shouldFlip`/`containerPadding`. A fixed max-height on the
            // Dialog (e.g. `100dvh`) is always taller than that real budget, so
            // the Dialog's own scrollbar never engages and the footer renders
            // below the fold with no way to reach Apply — the bottom-cutoff bug.
            // `overflow-hidden` clips to the rounded corners; the body below
            // scrolls instead, which keeps Cancel/Apply pinned and reachable.
            'flex flex-col overflow-hidden rounded-2xl bg-primary shadow-xl ring ring-secondary_alt',
            isEntering &&
              'duration-150 ease-out animate-in fade-in placement-bottom:slide-in-from-top-0.5',
            isExiting &&
              'duration-100 ease-in animate-out fade-out placement-bottom:slide-out-to-top-0.5'
          )
        }
        // zIndex must be inline — dynamic value from useStackingZIndex() context
        style={{ zIndex }}
      >
        <StackingLayer>
          <AriaDialog className='flex min-h-0 flex-1 flex-col outline-none'>
            {({ close }) => (
              <CalendarPickerDialog
                draftDate={draftDate}
                draftStartTime={draftStartTime}
                draftEndTime={draftEndTime}
                setDraftDate={setDraftDate}
                setDraftStartTime={setDraftStartTime}
                setDraftEndTime={setDraftEndTime}
                syncDraft={syncDraft}
                draftToDateRange={draftToDateRange}
                onChange={onChange}
                close={close}
              />
            )}
          </AriaDialog>
        </StackingLayer>
      </AriaPopover>
    </DialogTrigger>
  );
}
